import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PortfolioHighlights = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeCard, setActiveCard] = useState(0);

  const portfolioCompanies = [
  {
    id: 1,
    name: "Oneplacetwostories",
    logo: "https://images.unsplash.com/photo-1696950984481-779fd52d01a2",
    logoAlt: "Modern circular logo with geometric design in blue and white colors representing Oneplacetwostories brand",
    image: "https://images.unsplash.com/photo-1549194388-2469d59ec75c",
    imageAlt: "Modern e-commerce warehouse with organized shelving and digital inventory management systems",
    sector: "E-commerce Platform",
    founded: "2021",
    growth: "+340%",
    stage: "Series A",
    description: "Revolutionary e-commerce platform connecting European artisans with global markets through AI-powered matchmaking.",
    metrics: {
      revenue: "€2.4M ARR",
      users: "50K+ Active",
      markets: "12 Countries"
    }
  },
  {
    id: 2,
    name: "Mobellex",
    logo: "https://images.unsplash.com/photo-1681831179331-d9f7f8f65bbd",
    logoAlt: "Minimalist furniture logo with clean lines and modern typography in dark gray",
    image: "https://images.unsplash.com/photo-1670100279112-b9ece6e06c9b",
    imageAlt: "Elegant modern furniture showroom with minimalist design and natural lighting",
    sector: "Furniture Tech",
    founded: "2020",
    growth: "+280%",
    stage: "Seed",
    description: "Smart furniture marketplace leveraging AR technology for virtual home design and seamless delivery logistics.",
    metrics: {
      revenue: "€1.8M ARR",
      users: "35K+ Active",
      markets: "8 Countries"
    }
  },
  {
    id: 3,
    name: "Tiksaviems",
    logo: "https://images.unsplash.com/photo-1731335069412-b2a783ae6dac",
    logoAlt: "Tech startup logo featuring interconnected nodes in vibrant green and blue gradient",
    image: "https://images.unsplash.com/photo-1735469157670-1212e570eadc",
    imageAlt: "Modern tech office with collaborative workspaces and digital screens showing data analytics",
    sector: "FinTech SaaS",
    founded: "2022",
    growth: "+450%",
    stage: "Pre-Seed",
    description: "Next-generation financial analytics platform providing real-time insights for SME banking and investment decisions.",
    metrics: {
      revenue: "€800K ARR",
      users: "15K+ Active",
      markets: "5 Countries"
    }
  },
  {
    id: 4,
    name: "365nachrichten",
    logo: "https://images.unsplash.com/photo-1677981263394-c1810a9b0543",
    logoAlt: "News media logo with bold typography and newspaper-style design elements in red and black",
    image: "https://images.unsplash.com/photo-1719343881239-1d6cf7a44558",
    imageAlt: "Modern newsroom with journalists working at computers and large screens displaying breaking news",
    sector: "Media Tech",
    founded: "2021",
    growth: "+220%",
    stage: "Series A",
    description: "AI-powered news aggregation and personalization platform delivering hyper-local content across German-speaking markets.",
    metrics: {
      revenue: "€3.2M ARR",
      users: "120K+ Active",
      markets: "3 Countries"
    }
  }];


  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % portfolioCompanies?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const content = {
    en: {
      title: "Portfolio Success Stories",
      subtitle: "Discover how REXFEL transforms innovative ideas into market-leading companies",
      viewAll: "View Full Portfolio",
      founded: "Founded",
      sector: "Sector",
      growth: "Growth",
      investment: "Investment Stage"
    },
    de: {
      title: "Portfolio-Erfolgsgeschichten",
      subtitle: "Entdecken Sie, wie REXFEL innovative Ideen in marktführende Unternehmen verwandelt",
      viewAll: "Vollständiges Portfolio anzeigen",
      founded: "Gegründet",
      sector: "Sektor",
      growth: "Wachstum",
      investment: "Investitionsstufe"
    },
    fr: {
      title: "Histoires de Succès du Portfolio",
      subtitle: "Découvrez comment REXFEL transforme les idées innovantes en entreprises leaders du marché",
      viewAll: "Voir le Portfolio Complet",
      founded: "Fondée",
      sector: "Secteur",
      growth: "Croissance",
      investment: "Étape d\'Investissement"
    },
    es: {
      title: "Historias de Éxito del Portfolio",
      subtitle: "Descubre cómo REXFEL transforma ideas innovadoras en empresas líderes del mercado",
      viewAll: "Ver Portfolio Completo",
      founded: "Fundada",
      sector: "Sector",
      growth: "Crecimiento",
      investment: "Etapa de Inversión"
    }
  };

  const currentContent = content?.[currentLanguage];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {currentContent?.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {currentContent?.subtitle}
          </p>
          
          <Button
            variant="outline"
            iconName="ArrowRight"
            iconPosition="right">

            <Link to="/portfolio">
              {currentContent?.viewAll}
            </Link>
          </Button>
        </div>

        {/* Featured Company Carousel */}
        <div className="relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Company Details */}
            <div className="space-y-8 scroll-reveal">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-brand overflow-hidden shadow-brand-sm">
                  <Image
                    src={portfolioCompanies?.[activeCard]?.logo}
                    alt={portfolioCompanies?.[activeCard]?.logoAlt}
                    className="w-full h-full object-cover" />

                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {portfolioCompanies?.[activeCard]?.name}
                  </h3>
                  <p className="text-muted-foreground">
                    {portfolioCompanies?.[activeCard]?.sector}
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                {portfolioCompanies?.[activeCard]?.description}
              </p>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">{currentContent?.founded}</span>
                    <span className="font-medium text-foreground">{portfolioCompanies?.[activeCard]?.founded}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">{currentContent?.growth}</span>
                    <span className="font-bold text-success">{portfolioCompanies?.[activeCard]?.growth}</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">{currentContent?.investment}</span>
                    <span className="font-medium text-foreground">{portfolioCompanies?.[activeCard]?.stage}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Revenue</span>
                    <span className="font-bold text-primary">{portfolioCompanies?.[activeCard]?.metrics?.revenue}</span>
                  </div>
                </div>
              </div>

              {/* Performance Indicators */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-card rounded-brand-sm p-4 text-center border border-border">
                  <div className="text-lg font-bold text-primary">{portfolioCompanies?.[activeCard]?.metrics?.revenue}</div>
                  <div className="text-xs text-muted-foreground">Annual Revenue</div>
                </div>
                <div className="bg-card rounded-brand-sm p-4 text-center border border-border">
                  <div className="text-lg font-bold text-accent">{portfolioCompanies?.[activeCard]?.metrics?.users}</div>
                  <div className="text-xs text-muted-foreground">Active Users</div>
                </div>
                <div className="bg-card rounded-brand-sm p-4 text-center border border-border">
                  <div className="text-lg font-bold text-success">{portfolioCompanies?.[activeCard]?.metrics?.markets}</div>
                  <div className="text-xs text-muted-foreground">Markets</div>
                </div>
              </div>
            </div>

            {/* Company Image */}
            <div className="relative scroll-reveal">
              <div className="relative overflow-hidden rounded-brand shadow-brand-lg parallax-card">
                <Image
                  src={portfolioCompanies?.[activeCard]?.image}
                  alt={portfolioCompanies?.[activeCard]?.imageAlt}
                  className="w-full h-96 object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
                
                {/* Growth Badge */}
                <div className="absolute top-4 right-4">
                  <div className="bg-success text-success-foreground px-3 py-1 rounded-full text-sm font-bold shadow-brand-sm">
                    {portfolioCompanies?.[activeCard]?.growth} Growth
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center space-x-3 mt-12">
            {portfolioCompanies?.map((_, index) =>
            <button
              key={index}
              onClick={() => setActiveCard(index)}
              className={`w-3 h-3 rounded-full transition-brand ${
              index === activeCard ?
              'bg-primary' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
              } />

            )}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setActiveCard((prev) => (prev - 1 + portfolioCompanies?.length) % portfolioCompanies?.length)}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center shadow-brand hover:shadow-brand-lg transition-brand hover-lift focus-brand">

            <Icon name="ChevronLeft" size={20} />
          </button>
          
          <button
            onClick={() => setActiveCard((prev) => (prev + 1) % portfolioCompanies?.length)}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center shadow-brand hover:shadow-brand-lg transition-brand hover-lift focus-brand">

            <Icon name="ChevronRight" size={20} />
          </button>
        </div>
      </div>
    </section>);

};

export default PortfolioHighlights;